import { StyleSheet, Platform, Share, View } from "react-native";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { entitySync } from "@/services/sync/entity-sync";
import { entityCache } from "@/services/sync/entity-cache";
import { useAuth } from "@components/auth/useAuth";

import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";
import { MemorySelector } from "@/components/memory/MemorySelector";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyGroups, deleteGroup } from "@/services/firebase/group-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { GroupModel, ShareModel, ShareSummary, Contact, MemoryModel } from "common247";
import { GroupCard } from "@/components/groups/GroupCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterGroups, computeFilteredCounts } from "@/services/unified-filter-service";
import { downloadMediaFile } from "@/utils/media-download";

const GROUP_SORT_OPTIONS: ISortOption<GroupModel>[] = [
  { key: "name", label: "Group Name (A-Z)", getValue: (g) => g.name },
  { key: "owner", label: "Owner", getValue: (g) => g.ownerDisplayName || "" },
  { key: "memberCount", label: "Member Count", getValue: (g) => g.contactIds?.length || 0 },
  { key: "createdAt", label: "Date Created", getValue: (g) => g.createdAt || 0 },
];

const GROUP_GROUP_OPTIONS: IGroupOption<GroupModel>[] = [
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (g) => g.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "role",
    label: "Role",
    getGroupValue: (g) => (g.userRole === "owner" ? "My Groups (Owner)" : `Shared with me (${g.userRole || "Viewer"})`),
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (g) => {
      const tags = Object.values(g.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

export default function MyGroupsScreen() {
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();

  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loading, setLoading] = useState(true);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("groups");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isMemorySelectorOpen, setIsMemorySelectorOpen] = useState(false);

  // Sharing state
  const [sharingGroup, setSharingGroup] = useState<GroupModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [sharesByEntityId, setSharesByEntityId] = useState<Record<string, ShareModel[]>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [allGroups, setAllGroups] = useState<GroupModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const effectiveGroups = useMemo(() => {
    return filterGroups(groups, filter);
  }, [groups, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ groups }, filter, "groups");
  }, [groups, filter]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const fetchAllData = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const [response, contactsRes] = await Promise.all([
        getMyGroups(services, { filter }),
        getMyContacts(services, { includeInactive: false }).catch(() => ({ status: false, contacts: [] as Contact[] })),
      ]);
      if (response.status) {
        setGroups(response.groups || []);
        setAllGroups(response.groups || []);
        entityCache.registerEntities("group", response.groups || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load groups.");
      }
      if (contactsRes.status && contactsRes.contacts) {
        setContacts(contactsRes.contacts);
        entityCache.registerEntities("contact", contactsRes.contacts);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching groups.");
    } finally {
      setLoading(false);
    }
  }, [services, filter]);

  const fetchSharingData = async (groupId: string) => {
    if (!services) return;
    setLoadingShares(true);
    try {
      const [sharesRes, contactsRes] = await Promise.all([
        getSharesForSource(services, { sourceId: groupId, sourceType: "group" }),
        getMyContacts(services, { includeInactive: false }),
      ]);
      if (sharesRes.status) {
        setShares(sharesRes.shares);
        setSharesByEntityId((prev) => ({ ...prev, [groupId]: sharesRes.shares }));
        const updatedSummaries: ShareSummary[] = sharesRes.shares.map((s) => ({
          id: s.targetId,
          displayName: s.targetDisplayName || "Unknown",
          type: s.targetType,
          role: s.role,
          targetUserId: s.targetUserId,
        }));
        setGroups((prev) =>
          prev.map((g) => (g.id === groupId ? { ...g, sharedWith: updatedSummaries } : g))
        );
        setAllGroups((prev) =>
          prev.map((g) => (g.id === groupId ? { ...g, sharedWith: updatedSummaries } : g))
        );
      }
      if (contactsRes.status) setContacts(contactsRes.contacts);
    } catch (error) {
      console.error("Error fetching sharing data:", error);
    } finally {
      setLoadingShares(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchAllData();
    }, [fetchAllData])
  );

  useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "group") {
        if (event.action === "update" && event.data) {
          setGroups((prev) =>
            prev.map((g) => (g.id === event.id ? { ...g, ...event.data } : g))
          );
          setAllGroups((prev) =>
            prev.map((g) => (g.id === event.id ? { ...g, ...event.data } : g))
          );
        } else if (event.action === "delete") {
          setGroups((prev) => prev.filter((g) => g.id !== event.id));
          setAllGroups((prev) => prev.filter((g) => g.id !== event.id));
        } else {
          fetchAllData();
        }
      }
    });
    return unsubscribe;
  }, [fetchAllData]);

  const toggleItemSelection = (group: GroupModel) => {
    if (!group.id) return;
    const isSelected = selectedIds.includes(group.id);
    const newSelected = isSelected
      ? selectedIds.filter((id) => id !== group.id)
      : [...selectedIds, group.id];
    setSelectedIds(newSelected);
    if (newSelected.length === 0) {
      setSelectionMode(false);
    }
  };

  const handleItemPress = (group: GroupModel) => {
    if (isSelectionActive) {
      toggleItemSelection(group);
    } else {
      router.push(`/my-groups/${group.id}`);
    }
  };

  const handleItemLongPress = (group: GroupModel) => {
    if (!group.id) return;
    setSelectionMode(true);
    if (!selectedIds.includes(group.id)) {
      setSelectedIds((prev) => [...prev, group.id!]);
    }
  };

  const handleItemContextMenu = (group: GroupModel, _event?: any) => {
    handleItemLongPress(group);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  const handleSelectAll = () => {
    setSelectedIds(groups.map((g) => g.id!).filter(Boolean));
  };

  const getSelectedGroupsList = () => {
    return groups.filter((g) => selectedIds.includes(g.id!));
  };

  const handleBatchDownload = () => {
    const selected = getSelectedGroupsList();
    if (selected.length === 0) return;
    const jsonStr = JSON.stringify(
      selected.map((g) => ({
        id: g.id,
        name: g.name,
        description: g.description,
        memberCount: g.contactIds?.length || 0,
      })),
      null,
      2
    );
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(jsonStr)}`;
    downloadMediaFile(dataUri, "groups.json");
  };

  const handleBatchSendTo = async () => {
    const selected = getSelectedGroupsList();
    if (selected.length === 0) return;
    const summary = selected
      .map((g) => `${g.name}: ${g.description || `${g.contactIds?.length || 0} members`}`)
      .join("\n");
    try {
      if (Platform.OS !== "web") {
        await Share.share({ message: summary });
      } else if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title: "Groups", text: summary });
      } else {
        await Share.share({ message: summary });
      }
    } catch (err) {
      console.error("Error sharing groups:", err);
    }
  };

  const handleBatchDelete = () => {
    if (!services || selectedIds.length === 0) return;
    CustomAlert.alert(
      "Delete Groups",
      `Are you sure you want to delete ${selectedIds.length} selected group(s)? This action cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            let deletedCount = 0;
            for (const id of selectedIds) {
              try {
                const res = await deleteGroup(services, { groupId: id });
                if (res.status) deletedCount++;
              } catch (err) {
                console.error("Failed to delete group:", err);
              }
            }
            CustomAlert.alert("Deleted", `Deleted ${deletedCount} group(s).`);
            handleClearSelection();
            fetchAllData();
          },
        },
      ]
    );
  };

  const handleBatchShareConfirm = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services) return;
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const res = await share(services, {
          sourceId: id,
          sourceType: "group",
          targetId,
          targetType,
          role,
        });
        if (res.status) successCount++;
      } catch (err) {
        console.error("Failed to share group:", err);
      }
    }
    CustomAlert.alert(
      "Sharing Complete",
      `Shared ${successCount} group(s) with ${targetType}.`
    );
    handleClearSelection();
    fetchAllData();
  };

  const handleAddGroupsToMemory = async (targetMemory: MemoryModel) => {
    if (!services || !targetMemory.id) return;
    let sharedCount = 0;
    for (const groupId of selectedIds) {
      try {
        const res = await share(services, {
          sourceId: targetMemory.id,
          sourceType: "memory",
          targetId: groupId,
          targetType: "group",
          role: "viewer",
        });
        if (res.status) sharedCount++;
      } catch (err) {
        console.error("Error sharing memory with group:", err);
      }
    }
    CustomAlert.alert(
      "Memory Shared",
      `Shared "${targetMemory.title}" with ${sharedCount} selected group(s).`
    );
    handleClearSelection();
  };

  const batchActions: IBatchAction[] = [
    {
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: handleBatchDownload,
    },
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => setIsShareModalOpen(true),
    },
    {
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleBatchSendTo,
    },
    {
      id: "addToMemory",
      label: "Add to Memory",
      icon: "bookmark-outline",
      onPress: () => setIsMemorySelectorOpen(true),
      variant: "primary",
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: handleBatchDelete,
      variant: "destructive",
    },
  ];

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !sharingGroup) return;
    try {
      const res = await share(services, {
        sourceId: sharingGroup.id!,
        sourceType: "group",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchSharingData(sharingGroup.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingGroup) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchSharingData(sharingGroup.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share.");
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!services) return;
    const targetGroup = groups.find((g) => g.id === groupId);
    await confirmEntityDeletion({
      services,
      entityType: "group",
      entityId: groupId,
      entityName: targetGroup?.name,
      onProceedDelete: async (cascadeUnlink) => {
        setLoading(true);
        try {
          const res = await deleteGroup(services, { groupId, cascadeUnlink });
          if (!res.status) {
            setLoading(false);
          }
          return res;
        } catch (error) {
          setLoading(false);
          throw error;
        }
      },
      onSuccess: () => {
        fetchAllData();
      },
      onError: () => {
        setLoading(false);
      },
    });
  };

  const handleToggleInlineSharing = (item: GroupModel) => {
    if (sharingGroup?.id === item.id) {
      setSharingGroup(null);
    } else {
      setSharingGroup(item);
      fetchSharingData(item.id!);
    }
  };

  return (
    <ThemedScreen title="Groups" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="groups"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allGroups={groups}
          allContacts={contacts}
        />
        <ThemedList<GroupModel>
          data={effectiveGroups}
          sortOptions={GROUP_SORT_OPTIONS}
          defaultSortKey="name"
          defaultSortDirection="asc"
          groupOptions={GROUP_GROUP_OPTIONS}
          persistenceKey="my-groups"
          filterKeys={["name", "description", "ownerDisplayName"]}
          filterPlaceholder="Search groups..."
          loading={loading}
          onRefresh={fetchAllData}
          refreshing={loading}
          addButton={{
            title: isSelectionActive ? "Back" : "+ Add Group",
            onPress: () => (isSelectionActive ? handleClearSelection() : router.push("/my-groups/new")),
            variant: isSelectionActive ? "secondary" : "primary",
          }}
          onItemPress={handleItemPress}
          onItemLongPress={handleItemLongPress}
          onItemContextMenu={handleItemContextMenu}
          emptyText="No groups found."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, isHovered, viewMode) => {
            const isExpanded = sharingGroup?.id === item.id;
            const isSelected = selectedIds.includes(item.id!);

            return (
              <GroupCard
                group={item}
                allGroups={groups}
                currentUserId={user?.uid}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => handleItemPress(item)}
                onLongPress={() => handleItemLongPress(item)}
                onContextMenu={(e) => handleItemContextMenu(item, e)}
                onEditPress={() => router.push(`/my-groups/${item.id}`)}
                onDelete={() => handleDeleteGroup(item.id!)}
                isSharingExpanded={isExpanded}
                onToggleSharing={() => handleToggleInlineSharing(item)}
                shares={sharesByEntityId[item.id!] ?? (isExpanded ? shares : undefined)}
                availableContacts={contacts}
                availableGroups={allGroups.filter((g) => g.id !== item.id)}
                isLoadingShares={isExpanded && loadingShares}
                onShareTarget={handleShare}
                onUnshare={handleUnshare}
              />
            );
          }}
        />

        {/* Floating Contextual Batch Action Panel */}
        <ThemedActionHoverPanel
          visible={selectedIds.length > 0}
          selectedCount={selectedIds.length}
          totalCount={groups.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={batchActions}
        />

        {/* Batch Share Modal */}
        <ShareRecipientSelector
          visible={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="groups"
          onConfirmShare={handleBatchShareConfirm}
        />

        {/* Memory Selector Modal */}
        <MemorySelector
          visible={isMemorySelectorOpen}
          onClose={() => setIsMemorySelectorOpen(false)}
          selectedCount={selectedIds.length}
          itemTypeLabel="groups"
          onSelectMemory={handleAddGroupsToMemory}
          onCreateNewMemory={() => router.push("/my-memories/new")}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    paddingBottom: 0,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 40,
  },
});
